/**
 * 发货单 API 工厂函数
 * @module api/shipment/factory
 * @description 发货单相关的API请求函数，支持依赖注入
 * @last-updated 2025-12-26
 */

import { API_ROUTES } from '../routes';
import type { IRequestClient } from '../core/client';
import type {
  Shipment,
  ShipmentQuery,
  CreateShipmentForm,
  ConfirmShipmentForm,
  ConfirmReceivedForm,
  CancelShipmentForm,
  ShippableItem,
  AvailableBatch,
  ShipmentStatistics,
} from '../../types/shipment';
import type { PaginationResult } from '../../types/api';

/**
 * 发货单 API 前缀
 */
export const API_PREFIX = API_ROUTES.shipments.list;

/**
 * 创建发货单 API 实例
 * 
 * @param client - 请求客户端实例
 * @returns 发货单 API 方法集合
 * 
 * @example
 * ```typescript
 * import { createShipmentApi } from '@bayes-mes/shared/api/shipment';
 * 
 * const shipmentApi = createShipmentApi(httpClient);
 * 
 * // 获取发货单列表
 * const list = await shipmentApi.getList({ factory_id: 1 });
 * 
 * // 创建发货单
 * const shipment = await shipmentApi.create({
 *   factory_id: 1,
 *   sales_order_id: 100,
 *   items: [{ material_id: 1, planned_quantity: 10 }]
 * });
 * 
 * // 确认发货
 * await shipmentApi.confirm(shipment.id, {
 *   items: [{ id: 1, batch_no: 'BATCH001', actual_quantity: 10 }]
 * });
 * ```
 */
export function createShipmentApi(client: IRequestClient) {
  return {
    // ==================== 查询接口 ====================

    /**
     * 获取发货单列表（分页）
     */
    getList(params?: ShipmentQuery) {
      return client.get<PaginationResult<Shipment>>(API_ROUTES.shipments.list, { params });
    },

    /**
     * 获取发货单详情
     */
    getDetail(id: number) {
      return client.get<Shipment>(API_ROUTES.shipments.detail(id));
    },

    /**
     * 获取发货统计
     */
    getStatistics(params?: { factory_id?: number; start_date?: string; end_date?: string }) {
      return client.get<ShipmentStatistics>(API_ROUTES.shipments.statistics, { params });
    },

    /**
     * 获取销售订单的可发货物料
     */
    getShippableItems(salesOrderId: number) {
      return client.get<ShippableItem[]>(API_ROUTES.shipments.shippableItems(salesOrderId));
    },

    /**
     * 获取物料的可发货批次
     */
    getAvailableBatches(materialId: number, warehouseId?: number) {
      return client.get<AvailableBatch[]>(API_ROUTES.shipments.availableBatches(materialId), {
        params: { warehouse_id: warehouseId },
      });
    },

    // ==================== 操作接口 ====================

    /**
     * 创建发货单
     */
    create(data: CreateShipmentForm) {
      return client.post<Shipment>(API_ROUTES.shipments.shipments, data);
    },

    /**
     * 确认发货（执行库存扣减）
     */
    confirm(id: number, data: ConfirmShipmentForm) {
      return client.put<Shipment>(API_ROUTES.shipments.confirm(id), data);
    },

    /**
     * 确认签收
     */
    confirmReceived(id: number, data?: ConfirmReceivedForm) {
      return client.put<Shipment>(API_ROUTES.shipments.received(id), data);
    },

    /**
     * 取消发货单
     */
    cancel(id: number, data?: CancelShipmentForm) {
      return client.put<Shipment>(API_ROUTES.shipments.cancel(id), data);
    },
  };
}

/**
 * 发货单 API 类型
 */
export type ShipmentApi = ReturnType<typeof createShipmentApi>;
